import { NativeModules, Platform } from 'react-native';

export type SKANVersion = '2.0' | '2.2' | '3.0' | '4.0';

export interface SKANConversionRule {
  eventName: string;
  conditions?: Record<string, any>;
  conversionValue: number; // 0-63 for SKAN 3.0, 0-7 for fine values in SKAN 4.0
  coarseValue?: 'low' | 'medium' | 'high'; // SKAN 4.0 only
  lockWindow?: boolean; // SKAN 4.0 only
  priority?: number; // Higher priority rules override lower ones
  description?: string;
}

export interface SKANPresetConfig {
  name: 'subscriptions' | 'engagement' | 'iap';
  rules: SKANConversionRule[];
  description: string;
  maxValue: number; // Highest conversion value in this preset
}

export interface SKANPostback {
  version: string;
  adNetworkId: string;
  campaignId?: string;
  conversionValue?: number;
  coarseConversionValue?: string;
  sourceAppId?: string;
  timestamp: string;
  postbackSequenceIndex?: number; // SKAN 4.0
  didWin?: boolean; // SKAN 4.0
}

export interface SKANEventData {
  event: string;
  properties: Record<string, any>;
  matchedRule?: SKANConversionRule;
  previousValue?: number;
  newValue?: number;
  timestamp: string;
  ruleEvaluations?: Array<{ rule: string; matched: boolean; reason?: string }>;
}

export interface SKANMetrics {
  isSupported: boolean;
  version: SKANVersion;
  currentValue: number | null;
  currentPreset: string | null;
  ruleCount: number;
  evaluationCount: number;
  attributionData: SKANPostback | null;
  lastEvaluations: Array<{ event: string; rule?: string; timestamp: string }>;
}

/**
 * SKAN native module interface for iOS
 */
export interface SKANModuleInterface {
  /**
   * Check if SKAdNetwork is available
   */
  isAvailable(): Promise<boolean>;

  /**
   * Get current SKAN version supported
   */
  getVersion(): Promise<SKANVersion>;

  /**
   * Register app for attribution
   */
  registerAppForAdNetworkAttribution(): Promise<void>;

  /**
   * Update conversion value (SKAN 3.0)
   */
  updateConversionValue(value: number): Promise<void>;

  /**
   * Update postback conversion value (SKAN 4.0)
   */
  updatePostbackConversionValue(
    value: number,
    coarseValue?: 'low' | 'medium' | 'high',
    lockWindow?: boolean
  ): Promise<void>;

  /**
   * Start impression attribution for SKAN 4.0
   */
  startImpression(signature: string): Promise<void>;

  /**
   * End impression attribution for SKAN 4.0
   */
  endImpression(signature: string): Promise<void>;

  /**
   * Get current conversion value
   */
  getCurrentConversionValue(): Promise<number | null>;

  /**
   * Get attribution data if available
   */
  getAttributionData(): Promise<any>;
}

/**
 * Mock SKAN implementation
 */
class MockSKANModule implements SKANModuleInterface {
  private conversionValue = 0;
  private isRegistered = false;
  private attributionData: any = null;

  async isAvailable(): Promise<boolean> {
    return Platform.OS === 'ios';
  }

  async getVersion(): Promise<SKANVersion> {
    return '4.0'; // Mock latest version
  }

  async registerAppForAdNetworkAttribution(): Promise<void> {
    this.isRegistered = true;
    this.attributionData = {
      adNetworkId: 'mock.network.com',
      campaignId: '12345',
      sourceAppId: '123456789',
      timestamp: new Date().toISOString()
    };
    console.log('[Mock SKAN] App registered for attribution');
  }

  async updateConversionValue(value: number): Promise<void> {
    if (value >= 0 && value <= 63) {
      this.conversionValue = Math.max(this.conversionValue, value); // Only increase
      console.log(`[Mock SKAN] Conversion value updated to: ${value}`);
    } else {
      console.warn(`[Mock SKAN] Invalid conversion value: ${value}. Must be 0-63.`);
    }
  }

  async updatePostbackConversionValue(
    value: number,
    coarseValue?: 'low' | 'medium' | 'high',
    lockWindow?: boolean
  ): Promise<void> {
    this.conversionValue = Math.max(this.conversionValue, value);
    console.log(
      `[Mock SKAN] Postback value updated: ${value}, coarse: ${coarseValue}, lock: ${lockWindow}`
    );
  }

  async startImpression(signature: string): Promise<void> {
    console.log(`[Mock SKAN] Started impression: ${signature}`);
  }

  async endImpression(signature: string): Promise<void> {
    console.log(`[Mock SKAN] Ended impression: ${signature}`);
  }

  async getCurrentConversionValue(): Promise<number | null> {
    return this.conversionValue;
  }

  async getAttributionData(): Promise<any> {
    return this.attributionData;
  }
}

/**
 * Get SKAN module instance
 */
function getSKANModule(): SKANModuleInterface {
  if (Platform.OS === 'ios') {
    const nativeModule = NativeModules.LayersSKAN;
    if (nativeModule) {
      return nativeModule as SKANModuleInterface;
    }
  }
  return new MockSKANModule();
}

/**
 * SKAN Manager - High-level wrapper for SKAdNetwork functionality with rule engine
 */
export class SKANManager {
  private module: SKANModuleInterface;
  private onConversionUpdate?: (data: SKANEventData) => void;
  private currentPreset?: string;
  private rules: SKANConversionRule[] = [];
  private ruleEvaluationHistory: Array<{ event: string; rule?: string; timestamp: string }> = [];

  constructor(onConversionUpdate?: (data: SKANEventData) => void) {
    this.module = getSKANModule();
    if (onConversionUpdate) {
      this.onConversionUpdate = onConversionUpdate;
    }
  }

  /**
   * Initialize SKAN
   */
  async initialize(): Promise<void> {
    const isAvailable = await this.module.isAvailable();
    if (isAvailable) {
      await this.module.registerAppForAdNetworkAttribution();
    }
  }

  /**
   * Check if SKAN is supported
   */
  async isSupported(): Promise<boolean> {
    return this.module.isAvailable();
  }

  /**
   * Get SKAN version
   */
  async getVersion(): Promise<SKANVersion> {
    return this.module.getVersion();
  }

  /**
   * Set conversion rules preset
   */
  setPreset(preset: 'subscriptions' | 'engagement' | 'iap'): void {
    const presetConfig = this.getPresetConfig(preset);
    this.rules = presetConfig.rules.sort((a, b) => (b.priority || 0) - (a.priority || 0));
    this.currentPreset = preset;

    console.log(`SKAN preset set to: ${preset} (${this.rules.length} rules)`);
  }

  /**
   * Set custom conversion rules
   */
  setCustomRules(rules: SKANConversionRule[]): void {
    this.rules = rules.sort((a, b) => (b.priority || 0) - (a.priority || 0));
    this.currentPreset = 'custom';

    console.log(`SKAN custom rules set: ${rules.length} rules`);
  }

  /**
   * Process an event against conversion rules
   */
  async processEvent(eventName: string, properties: Record<string, any>): Promise<void> {
    if (!(await this.isSupported())) {
      return;
    }

    const ruleEvaluations: Array<{ rule: string; matched: boolean; reason?: string }> = [];
    let matchedRule: SKANConversionRule | undefined;

    // Find the highest priority matching rule
    for (const rule of this.rules) {
      const evaluation = this.evaluateRule(rule, eventName, properties);
      const ruleEvaluation: { rule: string; matched: boolean; reason?: string } = {
        rule: rule.description || `${rule.eventName} (${rule.conversionValue})`,
        matched: evaluation.matched
      };

      // Only add reason if it exists
      if (evaluation.reason) {
        ruleEvaluation.reason = evaluation.reason;
      }

      ruleEvaluations.push(ruleEvaluation);

      if (evaluation.matched && !matchedRule) {
        matchedRule = rule;
        break; // Take first match since rules are sorted by priority
      }
    }

    // Record evaluation history
    const historyEntry: { event: string; rule?: string; timestamp: string } = {
      event: eventName,
      timestamp: new Date().toISOString()
    };

    if (matchedRule?.description || matchedRule?.eventName) {
      historyEntry.rule = matchedRule.description || matchedRule.eventName;
    }

    this.ruleEvaluationHistory.push(historyEntry);

    // Keep only last 100 evaluations
    if (this.ruleEvaluationHistory.length > 100) {
      this.ruleEvaluationHistory = this.ruleEvaluationHistory.slice(-100);
    }

    if (matchedRule) {
      try {
        const previousValue = (await this.module.getCurrentConversionValue()) || 0;

        // Only update if new value is higher (SKAN rule)
        if (matchedRule.conversionValue > previousValue) {
          const version = await this.getVersion();

          if (version === '4.0' && matchedRule.coarseValue) {
            await this.module.updatePostbackConversionValue(
              matchedRule.conversionValue,
              matchedRule.coarseValue,
              matchedRule.lockWindow
            );
          } else {
            await this.module.updateConversionValue(matchedRule.conversionValue);
          }

          // Notify about conversion update
          if (this.onConversionUpdate) {
            this.onConversionUpdate({
              event: eventName,
              properties,
              matchedRule,
              previousValue,
              newValue: matchedRule.conversionValue,
              timestamp: new Date().toISOString(),
              ruleEvaluations
            });
          }

          console.log(
            `SKAN: Event ${eventName} matched rule '${matchedRule.description || matchedRule.eventName}', ` +
              `value: ${previousValue} → ${matchedRule.conversionValue}`
          );
        } else {
          console.log(
            `SKAN: Event ${eventName} matched rule but value ${matchedRule.conversionValue} ` +
              `not higher than current ${previousValue}`
          );
        }
      } catch (error) {
        console.error('SKAN conversion value update failed:', error);
      }
    }
  }

  /**
   * Evaluate if a rule matches an event
   */
  private evaluateRule(
    rule: SKANConversionRule,
    eventName: string,
    properties: Record<string, any>
  ): { matched: boolean; reason?: string } {
    // Check event name match
    if (rule.eventName !== eventName) {
      return { matched: false, reason: `Event name '${eventName}' != '${rule.eventName}'` };
    }

    // Check conditions if specified
    if (rule.conditions) {
      for (const [key, expectedValue] of Object.entries(rule.conditions)) {
        const actualValue = properties[key];

        if (typeof expectedValue === 'object' && expectedValue !== null) {
          // Handle comparison operators like { '>': 10, '<': 100 }
          for (const [operator, comparisonValue] of Object.entries(expectedValue)) {
            const result = this.evaluateCondition(actualValue, operator, comparisonValue);
            if (!result.matched) {
              return {
                matched: false,
                reason: `Condition ${key} ${operator} ${comparisonValue} failed (actual: ${actualValue})`
              };
            }
          }
        } else if (actualValue !== expectedValue) {
          // Direct equality check

          return {
            matched: false,
            reason: `Property ${key}: expected ${expectedValue}, got ${actualValue}`
          };
        }
      }
    }

    return { matched: true };
  }

  /**
   * Evaluate a single condition with operator
   */
  private evaluateCondition(
    actualValue: any,
    operator: string,
    expectedValue: any
  ): { matched: boolean } {
    const numericActual = Number(actualValue);
    const numericExpected = Number(expectedValue);

    switch (operator) {
      case '>':
        return { matched: numericActual > numericExpected };
      case '>=':
        return { matched: numericActual >= numericExpected };
      case '<':
        return { matched: numericActual < numericExpected };
      case '<=':
        return { matched: numericActual <= numericExpected };
      case '==':
      case '=':
        return { matched: actualValue === expectedValue };
      case '!=':
        return { matched: actualValue !== expectedValue };
      default:
        console.warn(`Unknown condition operator: ${operator}`);
        return { matched: false };
    }
  }

  /**
   * Get current conversion value
   */
  async getCurrentValue(): Promise<number | null> {
    return this.module.getCurrentConversionValue();
  }

  /**
   * Get attribution data
   */
  async getAttributionData(): Promise<any> {
    return this.module.getAttributionData();
  }

  /**
   * Get preset configuration
   */
  private getPresetConfig(preset: 'subscriptions' | 'engagement' | 'iap'): SKANPresetConfig {
    switch (preset) {
      case 'subscriptions':
        return {
          name: 'subscriptions',
          description: 'Optimized for subscription apps with trial-to-paid conversion tracking',
          maxValue: 63,
          rules: [
            {
              eventName: 'app_open',
              conversionValue: 1,
              priority: 1,
              description: 'App opened'
            },
            {
              eventName: 'screen_view',
              conditions: { screen_name: 'onboarding_complete' },
              conversionValue: 8,
              priority: 3,
              description: 'Onboarding completed'
            },
            {
              eventName: 'trial_start',
              conversionValue: 20,
              priority: 5,
              description: 'Free trial started'
            },
            {
              eventName: 'purchase_success',
              conditions: { revenue: { '<': 5 } },
              conversionValue: 35,
              priority: 7,
              description: 'Small purchase (<$5)'
            },
            {
              eventName: 'subscription_start',
              conversionValue: 50,
              priority: 10,
              description: 'Subscription started'
            },
            {
              eventName: 'subscription_renew',
              conversionValue: 63,
              priority: 15,
              description: 'Subscription renewed'
            }
          ]
        };

      case 'engagement':
        return {
          name: 'engagement',
          description: 'Optimized for engagement-driven apps focusing on user activity',
          maxValue: 63,
          rules: [
            {
              eventName: 'app_open',
              conversionValue: 1,
              priority: 1,
              description: 'App opened'
            },
            {
              eventName: 'content_open',
              conversionValue: 5,
              priority: 2,
              description: 'Content viewed'
            },
            {
              eventName: 'search',
              conversionValue: 12,
              priority: 4,
              description: 'Search performed'
            },
            {
              eventName: 'bookmark_add',
              conversionValue: 25,
              priority: 6,
              description: 'Content bookmarked'
            },
            {
              eventName: 'app_open',
              conditions: { session_count: { '>': 3 } },
              conversionValue: 40,
              priority: 8,
              description: 'Regular user (3+ sessions)'
            },
            {
              eventName: 'app_open',
              conditions: { session_count: { '>': 10 } },
              conversionValue: 63,
              priority: 12,
              description: 'Power user (10+ sessions)'
            }
          ]
        };

      case 'iap':
        return {
          name: 'iap',
          description: 'Optimized for in-app purchase revenue tracking',
          maxValue: 63,
          rules: [
            {
              eventName: 'app_open',
              conversionValue: 1,
              priority: 1,
              description: 'App opened'
            },
            {
              eventName: 'paywall_show',
              conversionValue: 8,
              priority: 2,
              description: 'Paywall shown'
            },
            {
              eventName: 'purchase_attempt',
              conversionValue: 15,
              priority: 3,
              description: 'Purchase attempted'
            },
            {
              eventName: 'purchase_success',
              conditions: { revenue: { '<': 1 } },
              conversionValue: 25,
              priority: 5,
              description: 'Small purchase (<$1)'
            },
            {
              eventName: 'purchase_success',
              conditions: { revenue: { '>=': 1, '<': 10 } },
              conversionValue: 40,
              priority: 7,
              description: 'Medium purchase ($1-$10)'
            },
            {
              eventName: 'purchase_success',
              conditions: { revenue: { '>=': 10 } },
              conversionValue: 63,
              priority: 10,
              description: 'Large purchase ($10+)'
            }
          ]
        };

      default:
        throw new Error(`Unknown preset: ${preset}`);
    }
  }

  /**
   * Get current preset name
   */
  getCurrentPreset(): string | null {
    return this.currentPreset || null;
  }

  /**
   * Get current rules
   */
  getCurrentRules(): SKANConversionRule[] {
    return [...this.rules];
  }

  /**
   * Get rule evaluation history
   */
  getEvaluationHistory(): Array<{ event: string; rule?: string; timestamp: string }> {
    return [...this.ruleEvaluationHistory];
  }

  /**
   * Check if SKAN 4.0 features are available
   */
  async supportsSKAN4(): Promise<boolean> {
    const version = await this.getVersion();
    return version === '4.0';
  }

  /**
   * Get SKAN metrics for debugging
   */
  async getSKANMetrics() {
    const isSupported = await this.isSupported();
    const version = await this.getVersion();
    const currentValue = await this.getCurrentValue();
    const attributionData = await this.getAttributionData();

    return {
      isSupported,
      version,
      currentValue,
      currentPreset: this.currentPreset || null,
      ruleCount: this.rules.length,
      evaluationCount: this.ruleEvaluationHistory.length,
      attributionData,
      lastEvaluations: this.ruleEvaluationHistory.slice(-5)
    };
  }
}

// Export singleton instance
export const SKAN = new SKANManager();

// Default export for convenience
export default SKAN;
